import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Plus, Minus, Check, Package, Shield, Truck, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useCart } from '../contexts/CartContext';
import { productsAPI } from '../services/api';
import ProductCard from '../components/products/ProductCard';

const ProductDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart, setIsCartOpen } = useCart();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await productsAPI.getBySlug(slug);
        setProduct(response.data);
        
        // Fetch related products
        const allProducts = await productsAPI.getAll({ category: response.data.category });
        setRelatedProducts(
          allProducts.data.filter(p => p.id !== response.data.id).slice(0, 4)
        );
      } catch (error) {
        console.error('Error fetching product:', error);
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Product not found</h1>
          <Link to="/products">
            <Button className="bg-amber-500 hover:bg-amber-600 text-black">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Products
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Get the current price based on selected variation
  const getCurrentPrice = () => {
    if (selectedVariation) {
      return selectedVariation.price;
    }
    return product.price;
  };

  // Check if product has variations
  const hasVariations = product?.variations && product.variations.length > 0;

  const handleAddToCart = () => {
    const cartProduct = {
      ...product,
      price: getCurrentPrice(),
      selectedVariation: selectedVariation?.name || null
    };
    addToCart(cartProduct, quantity, selectedVariation?.name || selectedOption);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const handleBuyNow = () => {
    const cartProduct = {
      ...product,
      price: getCurrentPrice(),
      selectedVariation: selectedVariation?.name || null
    };
    addToCart(cartProduct, quantity, selectedVariation?.name || selectedOption);
    setIsCartOpen(true);
  };

  return (
    <div className="min-h-screen bg-zinc-950 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back button */}
        <Button
          variant="ghost"
          className="text-zinc-400 hover:text-white mb-6"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {/* Product details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Image */}
          <div className="relative aspect-square rounded-2xl overflow-hidden bg-zinc-900 border border-zinc-800">
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-cover"
            />
            {product.badge && (
              <Badge 
                className={`absolute top-4 left-4 ${
                  product.badge === 'NEW' 
                    ? 'bg-emerald-500 text-white' 
                    : 'bg-amber-500 text-black'
                } font-bold text-sm px-3 py-1`}
              >
                {product.badge}
              </Badge>
            )}
          </div>

          {/* Info */}
          <div>
            {/* Category */}
            <Link 
              to={`/products?category=${product.category}`}
              className="text-amber-500 text-sm font-medium uppercase tracking-wide hover:underline"
            >
              {product.category.replace(/-/g, ' ')}
            </Link>

            {/* Name */}
            <h1 className="text-3xl md:text-4xl font-bold text-white mt-2 mb-4">
              {product.name}
            </h1>

            {/* Price */}
            <p className="text-amber-500 text-3xl font-bold mb-6">
              £{getCurrentPrice().toFixed(2)}
              {hasVariations && !selectedVariation && (
                <span className="text-lg text-zinc-500 font-normal ml-2">
                  (select option)
                </span>
              )}
            </p>

            {/* Description */}
            <p className="text-zinc-400 text-lg mb-6">
              {product.description}
            </p>

            {/* Product details */}
            {product.details && (
              <div className="bg-zinc-900 rounded-xl p-4 mb-6 border border-zinc-800">
                <h3 className="text-white font-semibold mb-3">Product Details</h3>
                <dl className="grid grid-cols-2 gap-2">
                  {Object.entries(product.details).map(([key, value]) => (
                    <React.Fragment key={key}>
                      <dt className="text-zinc-500 capitalize">{key}:</dt>
                      <dd className="text-zinc-300">{value}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              </div>
            )}

            {/* Product Variations */}
            {hasVariations && (
              <div className="mb-6">
                <label className="text-white font-medium block mb-3">Select Variation</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {product.variations.map((variation, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedVariation(variation)}
                      disabled={!variation.inStock}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        selectedVariation?.name === variation.name
                          ? 'border-amber-500 bg-amber-500/10'
                          : variation.inStock
                            ? 'border-zinc-700 hover:border-zinc-600 bg-zinc-800'
                            : 'border-zinc-800 bg-zinc-900 opacity-50 cursor-not-allowed'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`font-medium ${
                          selectedVariation?.name === variation.name ? 'text-amber-500' : 'text-white'
                        }`}>
                          {variation.name}
                        </span>
                        {!variation.inStock && (
                          <span className="text-xs text-red-400 bg-red-500/10 px-2 py-1 rounded">Out of Stock</span>
                        )}
                      </div>
                      <p className="text-amber-500 font-bold mt-1">
                        £{variation.price.toFixed(2)}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Options */}
            {product.options && product.options.length > 0 && (
              <div className="mb-6">
                <label className="text-white font-medium block mb-2">Select Option</label>
                <Select value={selectedOption || ''} onValueChange={setSelectedOption}>
                  <SelectTrigger className="w-full bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue placeholder="Choose an option" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {product.options.map((option) => (
                      <SelectItem key={option} value={option} className="text-white">
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Quantity */}
            <div className="mb-6">
              <label className="text-white font-medium block mb-2">Quantity</label>
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="icon"
                  className="border-zinc-700 text-white"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="text-white text-xl font-semibold w-12 text-center">
                  {quantity}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  className="border-zinc-700 text-white"
                  onClick={() => setQuantity(quantity + 1)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button
                className={`flex-1 h-12 font-semibold text-lg transition-all ${
                  added 
                    ? 'bg-green-600 hover:bg-green-600' 
                    : 'bg-amber-500 hover:bg-amber-600'
                } text-black`}
                onClick={handleAddToCart}
                disabled={(hasVariations && !selectedVariation) || (product.options && product.options.length > 0 && !selectedOption)}
              >
                {added ? (
                  <>
                    <Check className="h-5 w-5 mr-2" />
                    Added to Cart
                  </>
                ) : (
                  <>
                    <ShoppingCart className="h-5 w-5 mr-2" />
                    Add to Cart
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                className="flex-1 h-12 font-semibold text-lg border-zinc-700 text-white hover:bg-zinc-800"
                onClick={handleBuyNow}
                disabled={(hasVariations && !selectedVariation) || (product.options && product.options.length > 0 && !selectedOption)}
              >
                Buy Now
              </Button>
            </div>

            {/* Features */}
            <div className="grid grid-cols-3 gap-4">
              <div className="flex flex-col items-center text-center p-3 bg-zinc-900 rounded-lg border border-zinc-800">
                <Package className="h-5 w-5 text-amber-500 mb-1" />
                <span className="text-zinc-400 text-xs">Lab Tested</span>
              </div>
              <div className="flex flex-col items-center text-center p-3 bg-zinc-900 rounded-lg border border-zinc-800">
                <Shield className="h-5 w-5 text-amber-500 mb-1" />
                <span className="text-zinc-400 text-xs">Discreet</span>
              </div>
              <div className="flex flex-col items-center text-center p-3 bg-zinc-900 rounded-lg border border-zinc-800">
                <Truck className="h-5 w-5 text-amber-500 mb-1" />
                <span className="text-zinc-400 text-xs">UK 1-2 Days</span>
              </div>
            </div>

            {/* Research Disclaimer */}
            <div className="mt-6 bg-zinc-900/50 rounded-lg p-4 border border-zinc-800">
              <p className="text-zinc-500 text-xs leading-relaxed">
                <span className="text-zinc-400 font-semibold">⚗️ Research Use Only:</span> This product is sold strictly as a research chemical for in-vitro laboratory use only. It is not intended for human or animal consumption and is not sold as a drug, food supplement, or medicine. The buyer assumes full responsibility for lawful use. Must be 18+.{' '}
                <Link to="/terms" className="text-amber-500 hover:underline">Full disclaimer →</Link>
              </p>
            </div>
          </div>
        </div>

        {/* Related products */}
        {relatedProducts.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-white mb-6">Related Products</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetailPage;
